// ============================================================
// Planification horaire d'une tournée — logique PURE (ni Angular, ni HTTP, ni référentiel).
// ============================================================
//
// Quatre temps distincts du domaine, à ne jamais confondre :
//
//   askdatdel / askop / askslot  → la DEMANDE du client, figée à la commande
//   lockdatdel                   → la CONTRAINTE posée par le planificateur (ancre verrouillée)
//   plandatdel                   → l'ENGAGEMENT figé à la validation de la tournée
//   realdatdel                   → le RÉEL (BL signé)
//
// Ce module CALCULE, il ne persiste rien. `plandatdel` n'est JAMAIS une entrée du calcul :
// l'engagement d'hier ne doit pas devenir la contrainte d'aujourd'hui. Seul `lockdatdel` contraint.
//
// Unité de travail : minutes depuis MINUIT DU JOUR DE LA TOURNÉE. Une tournée qui déborde après
// minuit produit donc des valeurs >= 1440 (et non un retour à 0) : voir dayOffsetOf / toHhmm.

// Demande horaire FERME du client, traduite en fenêtre exploitable par le calcul.
// Seuls les opérateurs de NIVEAU 2 (Avant / Après / Horaire Fixe, appariés à un créneau horaire)
// produisent une fenêtre : le niveau 1 (Début / Milieu / Fin de matinée) est une préférence floue
// qui ne générerait que du bruit.
export type AskWindowKind = 'before' | 'after' | 'exact';

export interface AskWindow {
	kind: AskWindowKind;
	atMin: number;		// minutes depuis minuit du jour de la tournée
	label: string;		// « Avant 9h » — repris tel quel dans les messages
}

export interface ScheduleStopInput {
	legS: number | null;					// trajet VERS cet arrêt (secondes) ; null = inconnu (coordonnées manquantes)
	durationS: number | null;			// temps sur place ; null = defaultDurationS
	lockMin: number | null;				// contrainte du planificateur ; null = arrêt libre
	askWindow?: AskWindow | null;
	lockFromOtherDay?: boolean;		// la contrainte est datée d'un AUTRE jour que la tournée (livraison réaffectée)
}

export interface ScheduleInput {
	startMin: number | null;			// heure de départ du dépôt ; null = inconnue
	stops: ScheduleStopInput[];
	slackS: number;								// battement entre deux arrêts
	defaultDurationS: number;			// temps sur place par défaut
	exactToleranceMin?: number;		// tolérance d'un « Horaire Fixe » (défaut DEFAULT_EXACT_TOLERANCE_MIN)
}

// Diagnostics : le board AVERTIT, il n'interdit jamais — un planificateur peut sciemment
// poser une contrainte intenable (il négociera). Le silence, en revanche, n'est pas une option.
export type ScheduleIssue =
	| { kind: 'lock-before-start'; index: number; lockMin: number; startMin: number }
	| { kind: 'lock-not-monotonic'; index: number; lockMin: number; prevMin: number }
	| { kind: 'lock-unreachable'; index: number; lockMin: number; naturalMin: number }
	| { kind: 'lock-other-day'; index: number; lockMin: number }
	| { kind: 'lock-conflicts-ask'; index: number; lockMin: number; ask: AskWindow }
	| { kind: 'ask-missed'; index: number; arrivalMin: number; ask: AskWindow };

export interface ScheduleStopResult {
	arrivalMin: number | null;		// heure retenue : la contrainte si posée, sinon le calcul
	naturalMin: number | null;		// heure que donnerait la chaîne SANS la contrainte de cet arrêt
	impliedLegMin: number | null;	// trajet qu'IMPOSE la contrainte (départ du point précédent -> heure posée) ; null hors contrainte
	locked: boolean;
	issues: ScheduleIssue[];
}

export interface ScheduleResult {
	stops: ScheduleStopResult[];
	issues: ScheduleIssue[];			// à plat, dans l'ordre des arrêts — bandeau de tournée
}

export const DEFAULT_EXACT_TOLERANCE_MIN = 15;

// Heures d'arrivée d'une tournée + diagnostics, en un seul passage.
//
// Chaîne : départ + Σ (trajet + temps sur place + battement). Un tronçon inconnu (legS null)
// interrompt la chaîne — les arrêts suivants sont inconnus jusqu'à ce qu'une contrainte la répare.
// Une contrainte est une ancre ABSOLUE : l'arrivée vaut l'heure posée, l'horloge repart de là et
// le trajet entrant n'entre plus dans le calcul.
export function computeSchedule(input: ScheduleInput): ScheduleResult {
	const tolerance = input.exactToleranceMin ?? DEFAULT_EXACT_TOLERANCE_MIN;
	const stops: ScheduleStopResult[] = [];
	const all: ScheduleIssue[] = [];

	let t: number | null = input.startMin === null ? null : input.startMin * 60;		// horloge courante (secondes depuis minuit)
	let prevArrival: number | null = null;

	(input.stops ?? []).forEach((stop, index) => {
		// Heure de départ du point précédent (le dépôt au premier arrêt) : arrivée + temps sur place + battement.
		const departS: number | null = t === null ? null
			: t + (index > 0 ? (input.stops[index - 1].durationS ?? input.defaultDurationS) + input.slackS : 0);

		// Heure « naturelle » : ce que donne la chaîne compte tenu de l'amont (contraintes amont
		// comprises) mais SANS la contrainte de cet arrêt-ci. C'est la référence du diagnostic
		// « intenable » — et ce qu'on affiche au planificateur pour qu'il voie ce qu'il écrase.
		const naturalMin: number | null = (departS !== null && stop.legS !== null && stop.legS !== undefined)
			? Math.round((departS + stop.legS) / 60)
			: null;

		const issues: ScheduleIssue[] = [];
		const lockMin = stop.lockMin;
		const locked = lockMin !== null && lockMin !== undefined;
		const arrivalMin = locked ? lockMin! : naturalMin;

		// Le trajet que la contrainte LAISSE : du départ du point précédent à l'heure posée. C'est ce
		// qu'on montre en face du trajet réel barré — négatif, l'heure posée précède ce départ et
		// aucun trajet ne peut la tenir. Se lit même sans trajet connu : une ancre amont suffit.
		const impliedLegMin = locked && departS !== null ? Math.round(lockMin! - departS / 60) : null;

		if (locked) {
			t = lockMin! * 60;		// ancre absolue : l'horloge repart de l'heure posée

			if (stop.lockFromOtherDay) issues.push({ kind: 'lock-other-day', index, lockMin: lockMin! });
			if (input.startMin !== null && lockMin! < input.startMin) {
				issues.push({ kind: 'lock-before-start', index, lockMin: lockMin!, startMin: input.startMin });
			}
			if (prevArrival !== null && lockMin! < prevArrival) {
				// L'ordre des arrêts contredit les heures : c'est la séquence qu'il faut revoir.
				// « intenable » serait redondant ici (il découlerait du même écart) : on ne le double pas.
				issues.push({ kind: 'lock-not-monotonic', index, lockMin: lockMin!, prevMin: prevArrival });
			} else if (naturalMin !== null && naturalMin > lockMin!) {
				issues.push({ kind: 'lock-unreachable', index, lockMin: lockMin!, naturalMin });
			}
			if (stop.askWindow && violatesAsk(lockMin!, stop.askWindow, tolerance)) {
				issues.push({ kind: 'lock-conflicts-ask', index, lockMin: lockMin!, ask: stop.askWindow });
			}
		} else {
			t = naturalMin !== null ? naturalMin * 60 : null;		// chaîne interrompue : reste interrompue
			// Sans contrainte, c'est le PLAN qui rate la demande du client — l'alerte la plus utile
			// du board : elle apparaît sans que personne n'ait rien fixé.
			if (stop.askWindow && arrivalMin !== null && violatesAsk(arrivalMin, stop.askWindow, tolerance)) {
				issues.push({ kind: 'ask-missed', index, arrivalMin, ask: stop.askWindow });
			}
		}

		if (arrivalMin !== null) prevArrival = arrivalMin;
		stops.push({ arrivalMin, naturalMin, impliedLegMin, locked, issues });
		all.push(...issues);
	});

	return { stops, issues: all };
}

// Une heure respecte-t-elle la demande du client ? « Avant / Après » sont des inégalités strictes
// (la borne est la borne) ; « Horaire Fixe » admet une tolérance : arriver à 11h03 pour un 11h
// demandé n'est pas un incident.
function violatesAsk(min: number, ask: AskWindow, toleranceMin: number): boolean {
	switch (ask.kind) {
		case 'before': return min > ask.atMin;
		case 'after': return min < ask.atMin;
		case 'exact': return Math.abs(min - ask.atMin) > toleranceMin;
	}
}

// Message lisible d'un diagnostic (hors template : testable, et traduisible d'un seul endroit).
export function describeIssue(issue: ScheduleIssue): string {
	switch (issue.kind) {
		case 'lock-other-day':
			return `Heure fixée (${toHhmm(issue.lockMin)}) datée d'un autre jour que la tournée : à vérifier`;
		case 'lock-before-start':
			return `Heure fixée à ${toHhmm(issue.lockMin)}, avant le départ de la tournée (${toHhmm(issue.startMin)})`;
		case 'lock-not-monotonic':
			return `Heure fixée à ${toHhmm(issue.lockMin)}, avant l'arrêt précédent (${toHhmm(issue.prevMin)}) : revoir l'ordre des arrêts`;
		case 'lock-unreachable':
			return `Heure fixée à ${toHhmm(issue.lockMin)} intenable : au plus tôt ${toHhmm(issue.naturalMin)} (${issue.naturalMin - issue.lockMin} min de retard)`;
		case 'lock-conflicts-ask':
			return `Heure fixée à ${toHhmm(issue.lockMin)} en contradiction avec la demande du client (${issue.ask.label})`;
		case 'ask-missed':
			return `Arrivée ${toHhmm(issue.arrivalMin)} hors de la demande du client (${issue.ask.label})`;
	}
}

// ============================================================
// Conversions minutes ↔ affichage (pures, sans fuseau)
// ============================================================
// Les conversions minutes ↔ instants RFC3339 (isoAtWall / wallMinutesOf, isoAtMinutes /
// minutesFromMidnight) vivent dans @app/shared/time : elles dépendent du fuseau MÉTIER,
// ce module-ci n'en connaît aucun.

// minutes => « HH:mm » (valeur d'un <input type="time">). Une arrivée après minuit est rendue sur
// son heure murale : l'input ne sait pas porter le jour, dayOffsetOf le signale à côté.
export function toHhmm(min: number | null | undefined): string {
	if (min === null || min === undefined || isNaN(+min)) return '';
	const m = ((Math.round(+min) % 1440) + 1440) % 1440;
	return ('' + Math.floor(m / 60)).padStart(2, '0') + ':' + ('' + (m % 60)).padStart(2, '0');
}

// « 11:00 » (valeur d'un <input type="time">) => minutes depuis minuit ; '' ou invalide => null.
export function fromHhmm(hhmm: string | null | undefined): number | null {
	const m = /^(\d{1,2}):(\d{2})$/.exec((hhmm ?? '').trim());
	if (!m) return null;
	const h = +m[1], mi = +m[2];
	return (h < 24 && mi < 60) ? h * 60 + mi : null;
}

// Décalage de jour d'une heure d'arrivée : 0 = jour de la tournée, 1 = lendemain (badge « J+1 »).
export function dayOffsetOf(min: number | null | undefined): number {
	if (min === null || min === undefined || isNaN(+min)) return 0;
	return Math.floor(Math.round(+min) / 1440);
}

